import { useEffect, useRef, useState } from 'react';

/** Visualization of the customer-facing provisioning stages running in
 * real time. Stages are deliberately abstracted from the internal
 * orchestration — the homepage shouldn't be a tutorial on how the
 * sausage is made. Each stage fills its progress bar, the next
 * starts; loop with a brief pause once everything is green. Pauses
 * while off-screen. */

type Phase = { name: string; sub: string; ms: number };

// Customer-facing stages. The actual orchestration runs more steps
// behind the scenes; the homepage doesn't need to expose how the
// sausage is made.
const PHASES: Phase[] = [
  { name: 'Tenant connected',   sub: 'admin access verified',                ms: 1500 },
  { name: 'Domain configured',  sub: 'sending domain ready',                 ms: 1700 },
  { name: 'Mailboxes created',  sub: 'up to 150 sender addresses on your domain', ms: 2100 },
  { name: 'Sequencer connected',sub: 'every mailbox connected to your sequencer', ms: 1700 },
];

const TOTAL_MAILBOXES = 100;
const TOTAL_MS = PHASES.reduce((s, p) => s + p.ms, 0);
const REST_MS = 2200;  // pause once everything is green

export default function LivePipeline() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  // elapsed ms within the current cycle, in [0, TOTAL_MS + REST_MS).
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      es => es.forEach(e => setVisible(e.isIntersecting)),
      { threshold: 0.2 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      setElapsed(prev => {
        const next = prev + dt;
        return next > TOTAL_MS + REST_MS ? 0 : next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  // Per-phase progress in [0, 1] given elapsed.
  const phaseProgress: number[] = [];
  let acc = 0;
  for (const p of PHASES) {
    if (elapsed <= acc) phaseProgress.push(0);
    else if (elapsed >= acc + p.ms) phaseProgress.push(1);
    else phaseProgress.push((elapsed - acc) / p.ms);
    acc += p.ms;
  }
  const allDone = elapsed >= TOTAL_MS;
  // Mailbox counter rises in the back half (phases 4-7).
  const mailboxes = (() => {
    const startMs = PHASES[0].ms + PHASES[1].ms + PHASES[2].ms;        // begin counter at mailbox-creation
    const endMs   = TOTAL_MS;
    if (elapsed <= startMs) return 0;
    if (elapsed >= endMs)   return TOTAL_MAILBOXES;
    return Math.round(((elapsed - startMs) / (endMs - startMs)) * TOTAL_MAILBOXES);
  })();

  return (
    <div ref={ref} className="card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-line/60 bg-white/[0.02]">
        <span className={`w-2 h-2 rounded-full ${allDone ? 'bg-emerald-400' : 'bg-run animate-pulse'}`} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-dim">Tenant</div>
          <div className="text-sm font-medium text-ink truncate">getoutboundlift.io</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-dim">Mailboxes</div>
          <div className="text-sm font-mono tabular-nums text-ink">
            <span className={mailboxes > 0 ? 'text-emerald-400' : 'text-dim'}>{mailboxes}</span>
            <span className="text-dim"> / {TOTAL_MAILBOXES}</span>
          </div>
        </div>
      </div>

      {/* Phases */}
      <div className="p-5 space-y-3">
        {PHASES.map((phase, i) => {
          const prog = phaseProgress[i];
          const isActive = prog > 0 && prog < 1;
          const isDone = prog >= 1;
          return (
            <div key={phase.name}>
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <PhaseIcon active={isActive} done={isDone} idx={i} />
                  <span className={`text-sm ${isDone ? 'text-ink' : isActive ? 'text-ink' : 'text-dim'}`}>
                    {phase.name}
                  </span>
                </div>
                <span className="text-[10px] font-mono text-dim shrink-0">
                  {isDone ? 'done' : isActive ? `${Math.round(prog * 100)}%` : 'queued'}
                </span>
              </div>
              <div className="ml-5 mr-0 h-1 rounded-full bg-white/[0.04] overflow-hidden">
                <div
                  className={`h-full rounded-full transition-[width] duration-100 ease-linear ${
                    isDone   ? 'bg-emerald-400/80'
                  : isActive ? 'bg-gradient-to-r from-accent via-run to-emerald-400'
                  :            'bg-transparent'
                  }`}
                  style={{ width: `${prog * 100}%` }}
                />
              </div>
              <div className="ml-5 mt-1 text-[11px] text-dim">{phase.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-line/60 flex items-center justify-between text-[11px] font-mono">
        <span className="text-dim">step {Math.min(PHASES.length, phaseProgress.filter(p => p > 0).length)} of {PHASES.length}</span>
        <span className={allDone ? 'text-emerald-400' : 'text-run'}>
          {allDone ? '✓ ready to send' : 'provisioning…'}
        </span>
      </div>
    </div>
  );
}

function PhaseIcon({ active, done, idx }: { active: boolean; done: boolean; idx: number }) {
  if (done) {
    return (
      <span className="w-3.5 h-3.5 rounded-full bg-emerald-400/20 border border-emerald-400/60 flex items-center justify-center">
        <span className="text-emerald-400 text-[10px] leading-none">✓</span>
      </span>
    );
  }
  if (active) {
    return (
      <span className="w-3.5 h-3.5 rounded-full bg-run/20 border border-run/60 flex items-center justify-center">
        <span className="w-1.5 h-1.5 rounded-full bg-run animate-pulse" />
      </span>
    );
  }
  return (
    <span className="w-3.5 h-3.5 rounded-full bg-white/[0.03] border border-line flex items-center justify-center text-[9px] text-dim font-mono">
      {idx + 1}
    </span>
  );
}
